import { createFileRoute, Link, useRouter, notFound } from "@tanstack/react-router";
import { lastmodFor } from "@/lib/route-lastmod";
import { ORG_REF, PHYSICIAN_ID, PHYSICIAN_SAME_AS } from "@/lib/medical-schema";
import { useEffect } from "react";
import {
  getEsPostBySlug,
  getAdjacentEsPosts,
  prefetchEsPostHtml,
  type EsPost,
} from "@/lib/posts-es";
import { ShareButtons } from "@/components/site/ShareButtons";
import { EsPageShell } from "@/components/site/EsPageShell";
import {
  ES_PAGES,
  ES_BLOG_PREFIX,
  esCardUrl,
  esPostKeyForSlug,
  hreflangLinks,
  SITE,
} from "@/lib/es-pages";

export const Route = createFileRoute("/es/el-doctor-analiza-las-noticias_/$slug")({
  // Post content is immutable at build time — keep the loaded post in the
  // router cache so revisits skip the loader (and the HTML dynamic import).
  staleTime: Infinity,
  gcTime: 60 * 60_000, // 1 hour
  loader: async ({ params }): Promise<{ post: EsPost }> => {
    const post = await getEsPostBySlug(params.slug);
    if (!post) throw notFound();
    return { post };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return {
        meta: [
          { title: "Artículo no encontrado" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const { post } = loaderData;
    const key = esPostKeyForSlug(post.slug);
    const page = ES_PAGES[key];
    const url = `${SITE}${page.es}`;
    const image = esCardUrl(key);

    // Same brand-suffix rule as the English article route: seo-qa requires
    // every title to end " | Virginia Bariatric" and og/twitter to match it.
    const BRAND = "Virginia Bariatric";
    const withBrand = (t: string) =>
      t.includes(BRAND)
        ? t
        : `${t.replace(/\s*\|\s*(Dr\.? ?Fitzer( Dissects the News)?|El Dr\.? ?Fitzer|VA Bariatric|Virginia Bariatric)$/, "")} | ${BRAND}`;
    const title = withBrand(post.seoTitle ?? post.title);
    const description = post.seoDescription ?? post.excerpt?.slice(0, 155) ?? "";

    const meta = [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: post.ogTitle ?? title },
      { property: "og:description", content: post.ogDescription ?? description },
      { property: "og:url", content: url },
      { property: "og:type", content: "article" },
      { property: "og:locale", content: "es_LA" },
      { property: "og:locale:alternate", content: "en_US" },
      { property: "article:published_time", content: post.date },
      { property: "og:image", content: image },
      { property: "og:image:alt", content: page.ogImageAlt },
      { property: "og:image:type", content: "image/png" },
      { property: "og:image:width", content: "2400" },
      { property: "og:image:height", content: "1260" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: post.ogTitle ?? title },
      { name: "twitter:description", content: post.ogDescription ?? description },
      { name: "twitter:image", content: image },
      { name: "twitter:image:alt", content: page.ogImageAlt },
    ];

    const links = [
      { rel: "canonical", href: url },
      ...hreflangLinks(page),
      ...(post.featuredImage
        ? [
            {
              rel: "preload",
              as: "image",
              href: post.featuredImage,
              fetchPriority: "high",
            } as const,
          ]
        : []),
    ];

    const scripts = import.meta.env.SSR
      ? [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "NewsArticle",
              headline: post.title,
              description,
              datePublished: post.date,
              dateModified: lastmodFor(`/es/el-doctor-analiza-las-noticias/${post.slug}`) ?? post.date,
              mainEntityOfPage: { "@type": "WebPage", "@id": url },
              url,
              image: [
                {
                  "@type": "ImageObject",
                  contentUrl: image,
                  url: image,
                  caption: post.title,
                  width: 2400,
                  height: 1260,
                  representativeOfPage: true,
                },
                ...(post.images ?? []).map((img) => ({
                  "@type": "ImageObject",
                  contentUrl: `${SITE}${img.url}`,
                  url: `${SITE}${img.url}`,
                  name: img.name,
                  caption: img.caption,
                  description: img.description,
                  width: img.width,
                  height: img.height,
                  inLanguage: "es-US",
                })),
              ],
              inLanguage: "es-US",
              isPartOf: {
                "@type": "Blog",
                "@id": `${SITE}/es/el-doctor-analiza-las-noticias#blog`,
                name: "El Dr. Fitzer Analiza las Noticias",
                url: `${SITE}/es/el-doctor-analiza-las-noticias`,
              },
              author: {
                "@type": "Person",
                "@id": PHYSICIAN_ID,
                name: "Matthew Fitzer, MD",
                honorificSuffix: "MD",
                url: `${SITE}/about`,
                mainEntityOfPage: `${SITE}/about`,
                sameAs: PHYSICIAN_SAME_AS,
                jobTitle: "Bariatric Surgeon",
                knowsAbout: [
                  "Bariatric surgery",
                  "Obesity medicine",
                  "Weight-loss medicine",
                ],
                worksFor: ORG_REF,
              },
              publisher: {
                "@type": "Organization",
                "@id": `${SITE}/#organization`,
                name: "Virginia Bariatric Surgery",
                url: SITE,
                logo: {
                  "@type": "ImageObject",
                  url: `${SITE}/logo.png`,
                },
              },
            }),
          },
          // BreadcrumbList is emitted sitewide by __root.tsx — do not duplicate it here.
        ]
      : [];

    return { meta, links, scripts };
  },
  notFoundComponent: () => (
    <main className="mx-auto max-w-4xl px-6 py-16">
      <h1 className="mb-4 font-serif text-3xl">Artículo no encontrado</h1>
      <p className="text-muted-foreground">
        Es posible que este artículo se haya movido o eliminado.
      </p>
      <Link
        to="/es/el-doctor-analiza-las-noticias"
        className="mt-6 inline-block font-medium text-brand-mid hover:underline"
      >
        Volver a Todas las Publicaciones
      </Link>
    </main>
  ),
  component: EsPostPage,
});

function formatDateEs(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("es-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

function EsPostPage() {
  const { post } = Route.useLoaderData();
  const router = useRouter();
  const { prev, next } = getAdjacentEsPosts(post.slug);
  const pageKey = esPostKeyForSlug(post.slug);

  // Prefetch adjacent post HTML + route chunks during idle time so that
  // clicking Anterior/Siguiente feels instant without inflating the payload.
  useEffect(() => {
    const win = typeof window !== "undefined" ? window : null;
    if (!win) return;
    const ric: (cb: () => void) => number =
      (win as unknown as { requestIdleCallback?: (cb: () => void) => number })
        .requestIdleCallback ?? ((cb) => win.setTimeout(cb, 300));
    const cic: (h: number) => void =
      (win as unknown as { cancelIdleCallback?: (h: number) => void })
        .cancelIdleCallback ?? ((h) => win.clearTimeout(h));
    const handle = ric(() => {
      for (const adj of [prev, next]) {
        if (!adj) continue;
        prefetchEsPostHtml(adj.slug);
        void router.preloadRoute({
          to: "/es/el-doctor-analiza-las-noticias/$slug",
          params: { slug: adj.slug },
        });
      }
    });
    return () => cic(handle);
  }, [router, prev, next]);

  return (
    <EsPageShell page={pageKey} hero={<></>}>
      <div className="mx-auto max-w-4xl px-6 pt-2 pb-8">
        <nav className="mb-6 text-sm">
          <Link
            to="/es/el-doctor-analiza-las-noticias"
            className="text-brand-mid hover:underline"
          >
            ← Volver a Todas las Publicaciones
          </Link>
        </nav>

        <header>
          <h1 className="font-serif text-[1.9125rem] leading-tight text-brand-mid md:text-[2.55rem]">
            {post.title}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {formatDateEs(post.date)}
          </p>
          {post.subtitle && (
            <p className="mt-4 font-serif text-xl leading-snug text-brand-mid md:text-2xl">
              {post.subtitle}
            </p>
          )}
        </header>

        {post.featuredImage && (
          <figure className="mt-6">
            <img
              src={post.featuredImage}
              alt={post.featuredImageAlt ?? post.title}
              width={1600}
              height={729}
              className="aspect-video w-full h-auto rounded-md object-cover bg-muted"
              loading="eager"
              fetchPriority="high"
              decoding="async"
            />
            {post.featuredImageCaption && (
              <figcaption className="mt-2 text-center text-sm leading-snug text-muted-foreground">
                {post.featuredImageCaption}
              </figcaption>
            )}
          </figure>
        )}

        <article
          className="wp-post-content mt-8 leading-[1.6] md:text-[1.125rem] [&_p]:mb-4 [&_a]:text-brand-mid [&_a]:underline [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:text-brand-mid [&_h3]:font-serif [&_h3]:text-xl [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-brand-mid [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_img]:rounded-md [&_img]:my-6 [&_figure]:my-6 [&_figure_img]:my-0 [&_figcaption]:mt-2 [&_figcaption]:text-center [&_figcaption]:text-sm [&_figcaption]:leading-snug [&_figcaption]:text-muted-foreground [&_blockquote]:border-l-4 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-4 [&_sup]:text-[0.68em] [&_sup]:leading-none [&_sup]:align-super [&_sup]:ml-[0.1em] [&_sup_a]:no-underline [&_sup_a]:font-medium [&_sup_a]:hover:underline [&_.nowrap]:whitespace-nowrap [&_ol_li]:scroll-mt-28 [&_ol_li]:target:bg-brand-mid/10 [&_table]:my-6 [&_table]:w-full [&_table]:border-collapse [&_table]:text-sm [&_th]:border [&_th]:border-border [&_th]:bg-muted [&_th]:p-2 [&_th]:text-left [&_td]:border [&_td]:border-border [&_td]:p-2 [&_td]:align-top [&_tbody_th[scope=row]]:bg-transparent [&_tbody_th[scope=row]]:font-normal [&_a]:focus-visible:outline [&_a]:focus-visible:outline-2 [&_a]:focus-visible:outline-offset-2 [&_a]:focus-visible:outline-brand-mid [&_ol_li]:focus-visible:outline-none [&_ol_li]:focus:bg-brand-mid/10"
          dangerouslySetInnerHTML={{ __html: post.contentHtml }}
        />

        <ShareButtons
          url={`${SITE}${ES_BLOG_PREFIX}${post.slug}`}
          title={post.title}
          instagramImage={`${SITE}/og/es/og-${ES_PAGES[pageKey].card}-portrait.png`}
          lang="es"
        />

        {(prev || next) && (
          <nav
            aria-label="Más publicaciones"
            className="mt-12 grid gap-4 border-t pt-8 sm:grid-cols-2"
          >
            {prev ? (
              <Link
                to="/es/el-doctor-analiza-las-noticias/$slug"
                params={{ slug: prev.slug }}
                className="group block rounded-md border p-4 hover:border-brand-mid"
              >
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">
                  Anterior
                </span>
                <span className="mt-1 block font-serif text-lg text-brand-mid group-hover:underline">
                  {prev.title}
                </span>
              </Link>
            ) : (
              <span />
            )}
            {next ? (
              <Link
                to="/es/el-doctor-analiza-las-noticias/$slug"
                params={{ slug: next.slug }}
                className="group block rounded-md border p-4 text-right hover:border-brand-mid sm:text-right"
              >
                <span className="block text-xs uppercase tracking-wide text-muted-foreground">
                  Siguiente
                </span>
                <span className="mt-1 block font-serif text-lg text-brand-mid group-hover:underline">
                  {next.title}
                </span>
              </Link>
            ) : (
              <span />
            )}
          </nav>
        )}
      </div>
    </EsPageShell>
  );
}
